import React from 'react';
import PropTypes from 'prop-types';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import fr from 'facet-rest';
import { useRefreshAuthenticationFactors } from 'Hooks/useRefreshEvent';

const AuthenticationFactorContext = React.createContext(undefined);

// Actions
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';

const AUTHENTICATION_FACTOR_REFRESH_STARTED = 'authentication-factor-started';
const AUTHENTICATION_FACTOR_REFRESH_SUCCEEDED = 'authentication-factor-succeeded';
const AUTHENTICATION_FACTOR_REFRESH_FAILED = 'authentication-factor-refresh-failed';

const init = () => {
  return {
    isInitializing: true,
    initializingError: null,
    authenticationFactorCollection: null,
    isAuthenticationFactorRefreshing: false,
    authenticationFactorRefreshError: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        authenticationFactorCollection: action.authenticationFactorCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case AUTHENTICATION_FACTOR_REFRESH_STARTED: {
      return {
        ...state,
        isAuthenticationFactorRefreshing: true,
        authenticationFactorRefreshError: null,
      };
    }
    case AUTHENTICATION_FACTOR_REFRESH_SUCCEEDED: {
      return {
        ...state,
        isAuthenticationFactorRefreshing: false,
        authenticationFactorRefreshError: null,
        authenticationFactorCollection: action.authenticationFactorCollection,
      };
    }
    case AUTHENTICATION_FACTOR_REFRESH_FAILED: {
      return {
        ...state,
        isAuthenticationFactorRefreshing: false,
        authenticationFactorRefreshError: action.authenticationFactorRefreshError,
      };
    }
    default: {
      throw new Error(`Unhandled AuthenticationFactorContext action type: ${action.type}`);
    }
  }
};

const AuthenticationFactorProvider = ({
  children,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);
  const { pageContentResource } = usePageContentContext();

  const refreshAuthenticationFactorCollection = React.useCallback(async () => {
    const { authenticationFactorCollection: authenticationFactorColl } = state;

    if (authenticationFactorColl === null) {
      return;
    }

    try {
      dispatch({ type: AUTHENTICATION_FACTOR_REFRESH_STARTED });

      const coll = await authenticationFactorColl.link('self').get();

      dispatch({ type: AUTHENTICATION_FACTOR_REFRESH_SUCCEEDED, authenticationFactorCollection: coll });
    } catch (err) {
      dispatch({ type: AUTHENTICATION_FACTOR_REFRESH_FAILED, authenticationFactorRefreshError: err });
    }
  }, [state]);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      authenticationFactorCollection: state.authenticationFactorCollection,
      isAuthenticationFactorRefreshing: state.isAuthenticationFactorRefreshing,
      authenticationFactorRefreshError: state.authenticationFactorRefreshError,
      refreshAuthenticationFactorCollection,
    };
  }, [
    state,
    refreshAuthenticationFactorCollection,
  ]);

  const loadAuthenticationFactorCollection = async () => {
    try {
      dispatch({ type: INITIALIZE_STARTED });

      const resp = await pageContentResource.relationship(fr.relationships.authenticationFactor.name).link('related').get();

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        authenticationFactorCollection: resp,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, initializationError: err });
    }
  };

  useComponentDidMount(loadAuthenticationFactorCollection);

  useRefreshAuthenticationFactors(() => {
    refreshAuthenticationFactorCollection();
  }, [refreshAuthenticationFactorCollection]);

  return (
    <AuthenticationFactorContext.Provider value={memoizedValue}>
      {children}
    </AuthenticationFactorContext.Provider>
  );
};

// Use AuthenticationFactor State Context
const useAuthenticationFactorContext = () => {
  const context = React.useContext(AuthenticationFactorContext);
  if (context === undefined) {
    throw new Error('useAuthenticationFactorContext must be used within a AuthenticationFactorProvider');
  }
  return context;
};

AuthenticationFactorProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  AuthenticationFactorProvider,
  useAuthenticationFactorContext,
};
